/**
 * DIPPS Security Module
 * 
 * This module provides security measures for the application.
 * Uses Web Crypto API for AES-GCM encryption (stronger than XOR).
 * 
 * © 2024-2026 DIPPS. All Rights Reserved.
 */

// Generate a consistent key from a passphrase using PBKDF2
const ENCRYPTION_SALT = 'DIPPS_Salt_2024';
let cachedKey: CryptoKey | null = null;

async function deriveKey(): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;
  
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode('DIPPS_SecureKey_2024'),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  
  cachedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(ENCRYPTION_SALT),
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
  
  return cachedKey;
}

// Fallback XOR encryption for browsers without Web Crypto API
const FALLBACK_KEY = 'DIPPS_SecureKey_2024';

function xorEncrypt(text: string): string {
  try {
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(
        text.charCodeAt(i) ^ FALLBACK_KEY.charCodeAt(i % FALLBACK_KEY.length)
      );
    }
    return btoa(result);
  } catch {
    return text;
  }
}

function xorDecrypt(encoded: string): string {
  try {
    const text = atob(encoded);
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(
        text.charCodeAt(i) ^ FALLBACK_KEY.charCodeAt(i % FALLBACK_KEY.length)
      );
    }
    return result;
  } catch {
    return encoded;
  }
}

// Check if Web Crypto API is available
const hasWebCrypto = typeof crypto !== 'undefined' && 
  crypto.subtle && 
  typeof crypto.subtle.deriveKey === 'function';

async function aesEncrypt(text: string): Promise<string> {
  const key = await deriveKey();
  const encoder = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(text)
  );
  
  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + new Uint8Array(encrypted).length);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  
  return btoa(String.fromCharCode(...combined));
}

async function aesDecrypt(encoded: string): Promise<string> {
  const key = await deriveKey();
  const combined = new Uint8Array(atob(encoded).split('').map(c => c.charCodeAt(0)));
  
  const iv = combined.slice(0, 12);
  const data = combined.slice(12);
  
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    data
  );
  
  return new TextDecoder().decode(decrypted);
}

/**
 * Securely store data in localStorage with encryption
 * Uses AES-GCM when available, falls back to XOR
 */
export const secureStore = async (key: string, data: object | string): Promise<void> => {
  try {
    const jsonData = typeof data === 'string' ? data : JSON.stringify(data);
    
    if (hasWebCrypto) {
      const encrypted = await aesEncrypt(jsonData);
      localStorage.setItem(key, `aes:${encrypted}`);
    } else {
      const encrypted = xorEncrypt(jsonData);
      localStorage.setItem(key, `xor:${encrypted}`);
    }
  } catch (e) {
    console.error('[Security] Failed to store secure data:', e);
    // Fallback to XOR if AES fails
    try {
      const jsonData = typeof data === 'string' ? data : JSON.stringify(data);
      const encrypted = xorEncrypt(jsonData);
      localStorage.setItem(key, `xor:${encrypted}`);
    } catch {
      // Last resort: store unencrypted
      localStorage.setItem(key, typeof data === 'string' ? data : JSON.stringify(data));
    }
  }
};

/**
 * Retrieve and decrypt data from localStorage
 * Automatically detects encryption type
 */
export const secureRetrieve = async <T = string>(key: string, parseJson = true): Promise<T | null> => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    
    let decrypted: string;
    
    if (stored.startsWith('aes:')) {
      decrypted = await aesDecrypt(stored.slice(4));
    } else if (stored.startsWith('xor:')) {
      decrypted = xorDecrypt(stored.slice(4));
    } else {
      // Legacy data without prefix - try XOR first
      decrypted = xorDecrypt(stored);
    }
    
    return parseJson ? JSON.parse(decrypted) : decrypted as T;
  } catch (e) {
    console.error('[Security] Failed to retrieve secure data:', e);
    return null;
  }
};

/**
 * Securely remove data from localStorage
 */
export const secureRemove = (key: string): void => {
  localStorage.removeItem(key);
};

/**
 * Console warning - displayed once on app load
 */
const printConsoleWarning = () => {
  if (import.meta.env.DEV) return;
  
  console.log('%c🛑 STOP!', 'color: red; font-size: 60px; font-weight: bold;');
  console.log('%c⚠️ This is a protected application', 'color: orange; font-size: 20px;');
  console.log('%cUnauthorized access to this console is monitored and may be prosecuted.', 'color: red; font-size: 14px;');
  console.log('%c© 2024-2026 DIPPS - All Rights Reserved', 'color: gray; font-size: 12px;');
  console.log('%cIf you are a developer looking to help, please contact us at support@dipps.ca', 'color: blue; font-size: 12px;');
};

/**
 * Initialize security measures
 */
export const initSecurity = () => {
  if (import.meta.env.DEV) {
    console.log('🔓 Security measures disabled in development mode');
    return;
  }

  printConsoleWarning();
  console.log('🔒 Security measures initialized');
};

/**
 * Screenshot detection - basic visual logging only
 */
export const initScreenshotDetection = () => {
  if (import.meta.env.DEV) return;
  
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      console.log('📸 Tab switched or potential screenshot');
    }
  });
  
  console.log('📸 Screenshot detection initialized');
};

/**
 * Validate data integrity after retrieval
 */
export const validateDataIntegrity = <T>(data: unknown, schema: Record<keyof T, string>): boolean => {
  if (typeof data !== 'object' || data === null) return false;
  
  const obj = data as Record<string, unknown>;
  for (const key in schema) {
    if (!(key in obj) || typeof obj[key] !== schema[key]) {
      return false;
    }
  }
  return true;
};

/**
 * IP fetching utility - gets user's public IP
 */
let cachedIP: string | null = null;

export const getUserIP = async (): Promise<string> => {
  if (cachedIP) return cachedIP;
  
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    cachedIP = data.ip;
    return data.ip;
  } catch {
    return 'Unable to detect';
  }
};

/**
 * Right-click protection with intelligent watermark
 * Shows IP, legal warnings, and copyright notice
 */
interface ProtectionOverlayState {
  isVisible: boolean;
  ip: string;
  timestamp: string;
}

let overlayElement: HTMLDivElement | null = null;
let hideTimeout: ReturnType<typeof setTimeout> | null = null;

const createProtectionOverlay = (): HTMLDivElement => {
  const overlay = document.createElement('div');
  overlay.id = 'dipps-protection-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.95);
    z-index: 999999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    backdrop-filter: blur(10px);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;
  
  overlay.innerHTML = `
    <div style="text-align: center; max-width: 500px; padding: 40px;">
      <!-- Warning Triangle -->
      <div style="margin-bottom: 24px;">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#ff4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin: 0 auto; filter: drop-shadow(0 0 20px rgba(255,68,68,0.5));">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
      </div>
      
      <!-- Main Warning -->
      <h1 style="color: #ff4444; font-size: 28px; font-weight: bold; margin: 0 0 16px 0; text-transform: uppercase; letter-spacing: 2px;">
        ⚠️ WARNING ⚠️
      </h1>
      
      <h2 style="color: #ffffff; font-size: 20px; font-weight: 600; margin: 0 0 24px 0;">
        Protected Content Detected
      </h2>
      
      <!-- IP Display -->
      <div style="background: rgba(255, 68, 68, 0.15); border: 2px solid #ff4444; border-radius: 12px; padding: 16px; margin-bottom: 24px;">
        <p style="color: #ff6666; font-size: 12px; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 1px;">
          Your IP Address Has Been Logged
        </p>
        <p id="protection-ip" style="color: #ffffff; font-size: 24px; font-weight: bold; margin: 0; font-family: monospace;">
          Loading...
        </p>
        <p id="protection-timestamp" style="color: #888888; font-size: 11px; margin: 8px 0 0 0;">
          Timestamp: Loading...
        </p>
      </div>
      
      <!-- Legal Notice -->
      <div style="text-align: left; background: rgba(255, 255, 255, 0.05); border-radius: 12px; padding: 20px; margin-bottom: 24px;">
        <h3 style="color: #ffaa00; font-size: 14px; margin: 0 0 12px 0; display: flex; align-items: center; gap: 8px;">
          <span>📋</span> LEGAL NOTICE
        </h3>
        <ul style="color: #cccccc; font-size: 13px; margin: 0; padding-left: 20px; line-height: 1.8;">
          <li>This application is protected under <strong style="color: white;">Copyright Law</strong></li>
          <li>Unauthorized reproduction, distribution, or modification is <strong style="color: #ff4444;">strictly prohibited</strong></li>
          <li>Violators may face <strong style="color: #ff4444;">civil and criminal penalties</strong></li>
          <li>All access attempts are <strong style="color: #ffaa00;">monitored and logged</strong></li>
          <li>Maximum penalties include <strong style="color: #ff4444;">$150,000 per infringement</strong></li>
        </ul>
      </div>
      
      <!-- Copyright -->
      <div style="border-top: 1px solid rgba(255,255,255,0.1); padding-top: 20px;">
        <p style="color: #888888; font-size: 12px; margin: 0 0 8px 0;">
          © 2024-2026 DIPPS — All Rights Reserved
        </p>
        <p style="color: #666666; font-size: 11px; margin: 0;">
          Protected under the Digital Millennium Copyright Act (DMCA)<br>
          Canadian Copyright Act (R.S.C., 1985, c. C-42)
        </p>
      </div>
      
      <!-- Dismiss hint -->
      <p style="color: #555555; font-size: 11px; margin-top: 24px;">
        Click anywhere to dismiss • This incident has been recorded
      </p>
    </div>
  `;
  
  return overlay;
};

const showProtectionOverlay = async () => {
  if (!overlayElement) {
    overlayElement = createProtectionOverlay();
    document.body.appendChild(overlayElement);
  }
  
  // Clear any existing hide timeout
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }
  
  // Update IP and timestamp
  const ipElement = overlayElement.querySelector('#protection-ip');
  const timestampElement = overlayElement.querySelector('#protection-timestamp');
  
  if (ipElement) {
    const ip = await getUserIP();
    ipElement.textContent = ip;
  }
  
  if (timestampElement) {
    const now = new Date();
    timestampElement.textContent = `Timestamp: ${now.toISOString()} | Session: ${Math.random().toString(36).substring(7).toUpperCase()}`;
  }
  
  // Show overlay with animation
  overlayElement.style.pointerEvents = 'auto';
  requestAnimationFrame(() => {
    if (overlayElement) {
      overlayElement.style.opacity = '1';
    }
  });
  
  // Auto-hide after 5 seconds
  hideTimeout = setTimeout(() => {
    hideProtectionOverlay();
  }, 5000);
};

const hideProtectionOverlay = () => {
  if (overlayElement) {
    overlayElement.style.opacity = '0';
    overlayElement.style.pointerEvents = 'none';
  }
};

/**
 * Initialize right-click protection
 */
export const initRightClickProtection = () => {
  if (import.meta.env.DEV) {
    console.log('🔓 Right-click protection disabled in development mode');
    return;
  }
  
  // Prevent right-click context menu
  document.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    showProtectionOverlay();
    return false;
  });
  
  // Prevent common keyboard shortcuts for inspect/view-source
  document.addEventListener('keydown', (e) => {
    // Prevent F12
    if (e.key === 'F12') {
      e.preventDefault();
      showProtectionOverlay();
      return false;
    }
    
    // Prevent Ctrl+Shift+I (Inspect)
    if (e.ctrlKey && e.shiftKey && e.key === 'I') {
      e.preventDefault();
      showProtectionOverlay();
      return false;
    }
    
    // Prevent Ctrl+Shift+J (Console)
    if (e.ctrlKey && e.shiftKey && e.key === 'J') {
      e.preventDefault();
      showProtectionOverlay();
      return false;
    }
    
    // Prevent Ctrl+U (View Source)
    if (e.ctrlKey && e.key === 'u') {
      e.preventDefault();
      showProtectionOverlay();
      return false;
    }
    
    // Prevent Ctrl+Shift+C (Inspect Element)
    if (e.ctrlKey && e.shiftKey && e.key === 'C') {
      e.preventDefault();
      showProtectionOverlay();
      return false;
    }
  });
  
  // Click anywhere to dismiss overlay
  document.addEventListener('click', () => {
    if (overlayElement && overlayElement.style.opacity === '1') {
      hideProtectionOverlay();
    }
  });
  
  // Prevent text selection with CSS
  document.body.style.userSelect = 'none';
  document.body.style.webkitUserSelect = 'none';
  
  // Re-enable selection for input fields and textareas
  const style = document.createElement('style');
  style.textContent = `
    input, textarea, [contenteditable="true"], .selectable {
      user-select: text !important;
      -webkit-user-select: text !important;
    }
  `;
  document.head.appendChild(style);
  
  console.log('🛡️ Right-click protection initialized');
};

/**
 * Screenshot Protection System
 * Implements multiple deterrent measures against screenshots
 */
let screenshotOverlay: HTMLDivElement | null = null;
let isContentBlurred = false;

const createScreenshotWarningOverlay = (): HTMLDivElement => {
  const overlay = document.createElement('div');
  overlay.id = 'dipps-screenshot-warning';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%);
    z-index: 9999999;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.15s ease;
    pointer-events: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;
  
  overlay.innerHTML = `
    <div style="text-align: center; max-width: 550px; padding: 40px;">
      <!-- Camera with slash icon -->
      <div style="margin-bottom: 20px; position: relative;">
        <svg width="100" height="100" viewBox="0 0 24 24" fill="none" style="margin: 0 auto;">
          <rect x="2" y="6" width="20" height="14" rx="2" stroke="#ff4444" stroke-width="2"/>
          <circle cx="12" cy="13" r="4" stroke="#ff4444" stroke-width="2"/>
          <path d="M8 2L10 6" stroke="#ff4444" stroke-width="2" stroke-linecap="round"/>
          <path d="M16 2L14 6" stroke="#ff4444" stroke-width="2" stroke-linecap="round"/>
          <!-- Slash through camera -->
          <line x1="3" y1="22" x2="21" y2="4" stroke="#ff4444" stroke-width="3" stroke-linecap="round"/>
        </svg>
      </div>
      
      <h1 style="color: #ff4444; font-size: 32px; font-weight: bold; margin: 0 0 12px 0; text-transform: uppercase; letter-spacing: 3px; text-shadow: 0 0 30px rgba(255,68,68,0.5);">
        📸 SCREENSHOT BLOCKED
      </h1>
      
      <h2 style="color: #ffffff; font-size: 18px; font-weight: 500; margin: 0 0 24px 0;">
        Content Protection Active
      </h2>
      
      <div style="background: rgba(255, 68, 68, 0.1); border: 2px solid rgba(255, 68, 68, 0.5); border-radius: 16px; padding: 20px; margin-bottom: 24px;">
        <p id="screenshot-ip" style="color: #ff6666; font-size: 11px; margin: 0 0 4px 0; text-transform: uppercase; letter-spacing: 1px;">
          IP Address Logged
        </p>
        <p style="color: #ffffff; font-size: 20px; font-weight: bold; margin: 0; font-family: 'Courier New', monospace;" id="screenshot-ip-value">
          Detecting...
        </p>
      </div>
      
      <div style="background: rgba(255, 170, 0, 0.1); border-left: 4px solid #ffaa00; padding: 16px; text-align: left; margin-bottom: 20px;">
        <p style="color: #ffaa00; font-size: 14px; font-weight: bold; margin: 0 0 8px 0;">
          ⚠️ LEGAL WARNING
        </p>
        <p style="color: #cccccc; font-size: 12px; margin: 0; line-height: 1.6;">
          Screenshots, screen recordings, and reproductions of this application 
          are <strong style="color: #ff4444;">strictly prohibited</strong>. 
          Violations will be reported and may result in legal action under 
          copyright law. All attempts are logged with your IP address and timestamp.
        </p>
      </div>
      
      <p style="color: #666666; font-size: 11px; margin: 0;">
        © 2024-2026 DIPPS • Protected Content • All Rights Reserved
      </p>
    </div>
  `;
  
  return overlay;
};

const showScreenshotWarning = async () => {
  if (!screenshotOverlay) {
    screenshotOverlay = createScreenshotWarningOverlay();
    document.body.appendChild(screenshotOverlay);
  }
  
  // Update IP
  const ipElement = screenshotOverlay.querySelector('#screenshot-ip-value');
  if (ipElement) {
    const ip = await getUserIP();
    ipElement.textContent = ip;
  }
  
  // Show immediately
  screenshotOverlay.style.pointerEvents = 'auto';
  screenshotOverlay.style.opacity = '1';
  
  // Auto-hide after 3 seconds
  setTimeout(() => {
    if (screenshotOverlay) {
      screenshotOverlay.style.opacity = '0';
      screenshotOverlay.style.pointerEvents = 'none';
    }
  }, 3000);
};

const blurContent = () => {
  if (!isContentBlurred) {
    isContentBlurred = true;
    document.body.style.filter = 'blur(30px)';
    document.body.style.transition = 'filter 0.1s ease';
  }
};

const unblurContent = () => {
  if (isContentBlurred) {
    isContentBlurred = false;
    document.body.style.filter = 'none';
  }
};

/**
 * Initialize Screenshot Protection
 * Multiple layers of deterrent against screenshots
 */
export const initScreenshotProtection = () => {
  if (import.meta.env.DEV) {
    console.log('🔓 Screenshot protection disabled in development mode');
    return;
  }
  
  // 1. Detect PrintScreen key
  document.addEventListener('keydown', (e) => {
    // PrintScreen key
    if (e.key === 'PrintScreen' || e.code === 'PrintScreen') {
      e.preventDefault();
      blurContent();
      showScreenshotWarning();
      // Clear clipboard
      navigator.clipboard.writeText('Screenshot blocked by DIPPS').catch(() => {});
      setTimeout(unblurContent, 500);
      return false;
    }
    
    // Windows Snipping Tool (Win+Shift+S)
    if (e.metaKey && e.shiftKey && e.key === 'S') {
      e.preventDefault();
      blurContent();
      showScreenshotWarning();
      setTimeout(unblurContent, 500);
      return false;
    }
    
    // Mac screenshot shortcuts
    // Cmd+Shift+3 (full screen)
    if (e.metaKey && e.shiftKey && e.key === '3') {
      e.preventDefault();
      blurContent();
      showScreenshotWarning();
      setTimeout(unblurContent, 500);
      return false;
    }
    
    // Cmd+Shift+4 (selection)
    if (e.metaKey && e.shiftKey && e.key === '4') {
      e.preventDefault();
      blurContent();
      showScreenshotWarning();
      setTimeout(unblurContent, 500);
      return false;
    }
    
    // Cmd+Shift+5 (screenshot menu)
    if (e.metaKey && e.shiftKey && e.key === '5') {
      e.preventDefault();
      blurContent();
      showScreenshotWarning();
      setTimeout(unblurContent, 500);
      return false;
    }
  });
  
  // 2. Blur content when tab loses focus (potential screenshot indicator)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      blurContent();
      console.log('📸 Content protected - visibility changed');
    } else {
      // Small delay before unblurring to catch quick screenshot attempts
      setTimeout(unblurContent, 300);
    }
  });
  
  // 3. Blur on window blur (when user switches to another app)
  window.addEventListener('blur', () => {
    blurContent();
    console.log('📸 Content protected - window blur');
  });
  
  window.addEventListener('focus', () => {
    setTimeout(unblurContent, 200);
  });
  
  // 4. Add CSS protection layer
  const styleProtection = document.createElement('style');
  styleProtection.textContent = `
    /* Prevent drag and drop of images */
    img {
      -webkit-user-drag: none;
      user-drag: none;
      pointer-events: none;
    }
    
    /* Add watermark overlay */
    body::after {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      z-index: 999998;
      background: repeating-linear-gradient(
        45deg,
        transparent,
        transparent 100px,
        rgba(139, 90, 43, 0.02) 100px,
        rgba(139, 90, 43, 0.02) 200px
      );
    }
    
    /* Prevent printing */
    @media print {
      body {
        display: none !important;
      }
      html::before {
        content: 'Printing is disabled. © DIPPS - All Rights Reserved';
        display: block;
        padding: 100px;
        text-align: center;
        font-size: 24px;
        color: red;
      }
    }
  `;
  document.head.appendChild(styleProtection);
  
  // 5. Prevent copy/paste of images
  document.addEventListener('copy', (e) => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim() === '') {
      e.preventDefault();
      console.log('📸 Copy blocked');
    }
  });
  
  // 6. Click to dismiss screenshot warning
  document.addEventListener('click', () => {
    if (screenshotOverlay && screenshotOverlay.style.opacity === '1') {
      screenshotOverlay.style.opacity = '0';
      screenshotOverlay.style.pointerEvents = 'none';
    }
  });
  
  console.log('📸 Screenshot protection initialized');
};
